import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { MapPin, Briefcase, Plus, ExternalLink, ChevronRight, RotateCcw } from 'lucide-react';

function mapsLink(lat, lng) {
  return `https://www.google.com/maps?q=${lat},${lng}`;
}
function streetViewLink(lat, lng) {
  return `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`;
}

const STATUS_BADGE = {
  open:      { bg: 'bg-sky-100',     text: 'text-sky-700',     label: 'Open'      },
  completed: { bg: 'bg-gray-100',    text: 'text-gray-600',    label: 'Completed' },
  paid:      { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Paid'      },
  cancelled: { bg: 'bg-red-100',     text: 'text-red-700',     label: 'Cancelled' },
};

/**
 * OverviewTab — purely presentational.
 * Props: facility, jobs[], onNewWorkOrder(), onRepeatOrder(job)
 */
const OverviewTab = ({ facility, jobs = [], onNewWorkOrder, onRepeatOrder }) => {
  const history = useHistory();
  const { customerCompanyId } = useParams();

  const lat = facility.geoLocation?.lat;
  const lng = facility.geoLocation?.lng;

  return (
    <div className="space-y-6 p-6">
      {/* Location card */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="mb-4 text-sm font-semibold text-foreground flex items-center gap-2">
          <MapPin size={14} className="text-[#404880]" /> Location
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 text-sm">
          {facility.address && (
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Address</p>
              <p className="text-foreground">{facility.address}</p>
            </div>
          )}
          {(facility.city || facility.state) && (
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">City / State</p>
              <p className="text-foreground">{[facility.city, facility.state].filter(Boolean).join(', ')}</p>
            </div>
          )}
          {facility.zipcode && (
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">ZIP</p>
              <p className="text-foreground">{facility.zipcode}</p>
            </div>
          )}
          {facility.facilityType && (
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Type</p>
              <p className="text-foreground">{facility.facilityType}</p>
            </div>
          )}
          {facility.siteContact?.name && (
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Site Contact</p>
              <p className="text-foreground">{facility.siteContact.name}</p>
              {facility.siteContact.phone && (
                <p className="text-xs text-muted-foreground">{facility.siteContact.phone}</p>
              )}
            </div>
          )}
          {facility.notes && (
            <div className="sm:col-span-2 lg:col-span-3">
              <p className="text-xs text-muted-foreground mb-0.5">Notes</p>
              <p className="text-foreground text-sm whitespace-pre-wrap">{facility.notes}</p>
            </div>
          )}
        </div>
        {lat && lng && (
          <div className="mt-4 flex gap-4">
            <a href={mapsLink(lat, lng)} target="_blank" rel="noreferrer"
              className="flex items-center gap-1 text-xs text-[#404880] hover:underline">
              <ExternalLink size={11} /> Google Maps
            </a>
            <a href={streetViewLink(lat, lng)} target="_blank" rel="noreferrer"
              className="flex items-center gap-1 text-xs text-[#404880] hover:underline">
              <ExternalLink size={11} /> Street View
            </a>
          </div>
        )}
      </div>

      {/* Work orders card */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Briefcase size={14} className="text-[#404880]" />
            Work Orders
            {jobs.length > 0 && (
              <span className="rounded-full bg-[#404880]/10 px-2 py-0.5 text-[10px] font-semibold text-[#404880]">
                {jobs.length}
              </span>
            )}
          </h3>
          <button
            onClick={onNewWorkOrder}
            className="flex items-center gap-1 rounded-lg bg-[#CC457D] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#CC457D]/90 transition-colors"
          >
            <Plus size={12} /> New Work Order
          </button>
        </div>

        {jobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Briefcase size={28} className="mb-2 text-muted-foreground opacity-30" />
            <p className="text-sm text-muted-foreground">No work orders yet</p>
            <p className="text-xs text-muted-foreground opacity-60 mt-1">Create one to assign security coverage to this facility</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {jobs.map(job => {
              const cfg = STATUS_BADGE[job.status] || STATUS_BADGE.open;
              const agencyName = job.provider_company_id?.agency_name;
              return (
                <div
                  key={job._id}
                  className="flex items-center justify-between px-5 py-3.5 hover:bg-muted/50 transition-colors"
                >
                  {/* Left: job info + status label */}
                  <button
                    onClick={() => history.push(`/customer-company/${customerCompanyId}/jobs/${job._id}`)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-sm font-medium text-foreground hover:text-[#404880] transition-colors">{job.serviceType || 'Security Service'}</p>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${cfg.bg} ${cfg.text}`}>
                        {cfg.label}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {agencyName && <><span>{agencyName}</span> · </>}
                      {job.shiftStats?.total ?? 0} shifts
                    </p>
                  </button>

                  {/* Right: action buttons only */}
                  <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                    {onRepeatOrder && (
                      <button
                        onClick={() => onRepeatOrder(job)}
                        title="Repeat this work order"
                        className="flex items-center gap-1 rounded-md bg-[#CC457D]/10 border border-[#CC457D]/30 px-2 py-1 text-[10px] font-semibold text-[#CC457D] hover:bg-[#CC457D] hover:text-white transition-colors"
                      >
                        <RotateCcw size={10} /> Repeat
                      </button>
                    )}
                    <button
                      onClick={() => history.push(`/customer-company/${customerCompanyId}/jobs/${job._id}`)}
                      className="flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-[10px] font-semibold text-gray-500 hover:border-[#404880] hover:text-[#404880] transition-colors"
                    >
                      Open <ChevronRight size={10} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default OverviewTab;
